<template>
  <div class="hosted-account-selector">
    <SelectComponent
      v-model="selectedCountry"
      :options="countryOptions"
      :isLoading="internalLoading"
      :isError="isError"
      :placeholder="$t('hosted_accounts.select_country_placeholder')"
    />
  </div>
</template>

<script setup lang="ts">
import SelectComponent from "@/components/common/SelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { getRegistryLabel } from "@/utils/registries";
import type {
  DropdownOption,
  SolderaRegistryAccount,
} from "@/dashboard/inventory_management/company_orders/types/virtualAccountTypes";
import type { Company } from "@/types";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { fetchSolderaRegistryAccounts } from "../common/registryApi";

const props = defineProps<{
  company: Company;
  selectedCountry?: string;
}>();

const emit = defineEmits<{
  "update:selectedCountry": [value: string];
  "accounts-loaded": [accounts: SolderaRegistryAccount[]];
}>();

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const selectedCountry = ref<string>(props.selectedCountry ?? "");
const registryAccounts = ref<SolderaRegistryAccount[]>([]);
const internalLoading = ref(false);
const isError = ref(false);

const unlockedRegions = computed(
  () => props.company.unlocked_hosted_account_regions ?? []
);

const accountsByCountry = computed(() => {
  const map = new Map<string, SolderaRegistryAccount>();
  registryAccounts.value.forEach((acc) => {
    map.set(acc.country_code, acc);
  });
  return map;
});

const countryOptions = computed<DropdownOption[]>(() =>
  [...unlockedRegions.value]
    .sort()
    .filter((code) => accountsByCountry.value.has(code))
    .map((code) => {
      const account = accountsByCountry.value.get(code)!;
      const registryLabel = getRegistryLabel(account.country_code, t);
      return {
        label: registryLabel,
        value: code,
        description: `<span class="badge bg-primary">${t("hosted_accounts.hosted_account")}</span>`,
      };
    })
);

const loadRegistryAccounts = async () => {
  if (registryAccounts.value.length > 0) {
    return;
  }

  internalLoading.value = true;
  isError.value = false;

  try {
    const response = await fetchSolderaRegistryAccounts();
    registryAccounts.value = response.accounts;
    emit("accounts-loaded", response.accounts);
  } catch (error) {
    globalErrorHandler(error);
    isError.value = true;
  } finally {
    internalLoading.value = false;
  }
};

watch(selectedCountry, (newValue) => {
  emit("update:selectedCountry", newValue);
});

watch(
  () => props.selectedCountry,
  (newValue) => {
    if (newValue !== undefined && newValue !== selectedCountry.value) {
      selectedCountry.value = newValue;
    }
  }
);

watch(
  [unlockedRegions, countryOptions],
  () => {
    const allowedValues = new Set(countryOptions.value.map((o) => o.value));
    if (selectedCountry.value && !allowedValues.has(selectedCountry.value)) {
      selectedCountry.value = "";
    }

    const unlocked = unlockedRegions.value;
    if (unlocked.length !== 1 || countryOptions.value.length === 0) {
      return;
    }
    const only = unlocked[0];
    if (
      countryOptions.value.some((o) => o.value === only) &&
      selectedCountry.value !== only
    ) {
      selectedCountry.value = only;
    }
  },
  { flush: "post" }
);

onMounted(() => {
  void loadRegistryAccounts();
});
</script>
